/**
* Copyright by:

* by ESS-Ehrhardt Software Solutions & Service GmbH
* Basselscheider Str. 55
* D-56281 Emmelshausen
* +49 (0) 67 42 / 87 27 0
* info@epg.com
*/
import { ChipsElementModel } from '../../../../app/shared/chips-autocomplete/chips-autocomplete.component';
import { OneAppButtonConfig } from '../../../../app/shared/oneapp-header/oneapp-header.component';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Logger, LogService } from '@epg/angular-base-lib';
import { ButtonAction, ConfirmDeleteDialogData, ConfirmDialogService } from '@epg/ng-components';
import { faSave, faSpinnerThird } from '@fortawesome/pro-regular-svg-icons';
import { Subscription } from 'rxjs';
import { opencanDeactivateDialog, toTitleCase } from '../../../../app/shared/functions';
import { OneAppPermission } from '../../../../enums/permission.enum';
import { EmployeeGroupModel } from '../../../../models/employee-group.model';
import { EmployeeModel } from '../../../../models/employee.model';
import { EmployeeService } from '../../../../services/employee.service';
import { SnackbarService } from '../../../../services/snackbar.service';
import { CanComponentDeactivate } from '@/app/shared/interface/can-component-deactivate.interface';

const SAVE_CALL_BACK_IDENTIFIER = 'save';
const DELETE_CALL_BACK_IDENTIFIER = 'delete';

const EMPLOYEES_TEXT = $localize`:@@employeesLabel:Employees`;
const SUPERVISORS_TEXT = $localize`:@@supervisorsLabel:Supervisors`;

const EMPLOYEES_AUTOCOMPLETE_PLACEHOLDER = $localize`:@@addEmployees:Add employees`;
const SUPERVISORS_AUTOCOMPLETE_PLACEHOLDER = $localize`:@@addSupervisors:Add supervisors`;

const EMPLOYEE_DETAILS_URL = 'Administration/employee/';
@Component({
  selector: 'app-employee-group-detail',
  templateUrl: './employee-group-detail.component.html',
  styleUrls: ['./employee-group-detail.component.scss'],
  standalone: false
})
export class EmployeeGroupDetailComponent implements OnInit, OnDestroy, CanComponentDeactivate {
  routeSub: Subscription = new Subscription();
  employeesChangedSub: Subscription = new Subscription();
  employeeGroupsChangedSub: Subscription = new Subscription();
  employeeRouteChangeSub: Subscription = new Subscription();
  changeRouteSub: Subscription = new Subscription();
  formChangesSubscription!: Subscription;
  OneAppPermission = OneAppPermission;

  EMPLOYEES_TEXT = EMPLOYEES_TEXT;
  SUPERVISORS_TEXT = SUPERVISORS_TEXT;
  EMPLOYEES_AUTOCOMPLETE_PLACEHOLDER = EMPLOYEES_AUTOCOMPLETE_PLACEHOLDER;
  SUPERVISORS_AUTOCOMPLETE_PLACEHOLDER = SUPERVISORS_AUTOCOMPLETE_PLACEHOLDER;
  faSpinnerThird = faSpinnerThird;

  employeeGroupForm: FormGroup;
  employeeGroup!: EmployeeGroupModel;
  allEmployees: EmployeeModel[] = [];
  currentEmpGroupID = '';

  editMode = true;
  loadingEmployeeGroup = true;
  onEmployeeGroupChanging = false;

  buttonAction = ButtonAction;
  saveButtonConfig: OneAppButtonConfig = {
    id: 'save_employee_group',
    text: $localize`:save@@save:save`,
    disabled: true,
    callBackIdentifier: SAVE_CALL_BACK_IDENTIFIER,
    icon: faSave,
    requiredPermissions: [OneAppPermission.EMPLOYEE_WRITE]
  };

  deleteButtonConfig: OneAppButtonConfig = {
    id: 'delete_employee_group',
    callBackIdentifier: DELETE_CALL_BACK_IDENTIFIER,
    disabled: true,
    requiredPermissions: [OneAppPermission.EMPLOYEE_DELETE]
  };
  convertedEmployees!: ChipsElementModel[];

  private log: Logger;

  constructor(
    private employeeService: EmployeeService,
    private router: Router,
    private route: ActivatedRoute,
    private logService: LogService,
    private readonly dialogService: ConfirmDialogService,
    private snackbarService: SnackbarService
  ) {
    this.log = logService.getLogger(this);
    this.employeeGroupForm = new FormGroup({
      employees: new FormControl([]),
      supervisors: new FormControl([]),
      name: new FormControl('', Validators.required)
    });
    this.setEmployeeGroupForm();
  }
  async ngOnInit(): Promise<void> {
    this.loadingEmployeeGroup = true;
    this.allEmployees = await this.employeeService.getEmployees();
    this.employeesChangedSub = this.employeeService.employeesChanged.subscribe((employees) => {
      this.allEmployees = employees;
      this.convertedEmployees = this.convertToChipsElementModelArray(this.allEmployees);
    });
    this.formChangesSubscription = this.employeeGroupForm.valueChanges.subscribe(() =>
      this.onFormChanged()
    );
    this.employeeGroupsChangedSub = this.employeeService.employeeGroupsChanged.subscribe(
      async (x) => {
        this.loadingEmployeeGroup = true;
        this.deleteButtonConfig.disabled = true;
        const empGroup = this.employeeService.getEmployeeGroup(this.currentEmpGroupID);
        if (empGroup != null) {
          if (empGroup.employees.length === 0) {
            empGroup.employees = await this.getEmployeesInEmployeeGroup();
          }
          this.employeeGroup = empGroup;
          this.setEmployeeGroupForm();
          this.loadingEmployeeGroup = false;
          this.employeeGroupForm.markAsPristine();
        }
      }
    );

    // change url on adding and deleting employees
    this.changeRouteSub = this.employeeService.employeeGroupRouteChange.subscribe((url) => {
      this.router.navigate([url], { relativeTo: this.route.parent });
    });

    // subscribe to routes and load screen
    this.routeSub = this.route.params.subscribe(async (params) => {
      this.loadingEmployeeGroup = true;
      this.deleteButtonConfig.disabled = true;
      if (params['employeeGroupId'] === 'new') {
        this.editMode = false;
        this.currentEmpGroupID = '';
        this.employeeGroup = this.getNewEmployeeGroupObj();

        this.allEmployees = await this.employeeService.getEmployees();
        this.convertedEmployees = this.convertToChipsElementModelArray(this.allEmployees);
        this.employeeGroupForm.reset();
      } else {
        this.editMode = true;
        this.currentEmpGroupID = params['employeeGroupId'];

        const empGroup = this.employeeService.getEmployeeGroup(this.currentEmpGroupID);
        if (empGroup) {
          if (empGroup.employees.length === 0) {
            empGroup.employees = await this.getEmployeesInEmployeeGroup();
          }
          this.employeeGroup = empGroup;
        } else {
          this.employeeGroup = this.getNewEmployeeGroupObj();
        }
        this.allEmployees = await this.employeeService.getEmployees();
        this.convertedEmployees = this.convertToChipsElementModelArray(this.allEmployees);
        this.deleteButtonConfig.disabled = false;
        this.employeeGroupForm.reset();
      }
      this.setEmployeeGroupForm();
      this.loadingEmployeeGroup = false;
    });
  }
  ngOnDestroy(): void {
    this.routeSub.unsubscribe();
    this.employeesChangedSub.unsubscribe();
    this.employeeGroupsChangedSub.unsubscribe();
    this.changeRouteSub.unsubscribe();
    this.formChangesSubscription.unsubscribe();
  }

  //form related methods
  onFormChanged(): void {
    this.saveButtonConfig.disabled = !(
      this.employeeGroupForm.valid && this.employeeGroupForm.dirty
    );
  }

  async setEmployeeGroupForm() {
    this.employeeGroupForm.reset();

    if (!this.employeeGroup) {
      this.employeeGroup = this.getNewEmployeeGroupObj();
    } else {
      this.employeeGroupForm.controls.supervisors.setValue([
        ...(this.employeeGroup.supervisors ?? [])
      ]);
      this.employeeGroupForm.controls.employees.setValue([...(this.employeeGroup.employees ?? [])]);
      this.employeeGroupForm.controls.name.setValue(this.employeeGroup.name ?? '');
    }
    this.loadingEmployeeGroup = false;
  }
  async onSubmit() {
    this.saveButtonConfig.disabled = true;

    const empGroup: EmployeeGroupModel = {
      employeeGroupId: this.employeeGroup.employeeGroupId,
      name: this.employeeGroupForm.value.name,
      employees: this.employeeGroupForm.value.employees,
      supervisors: this.employeeGroupForm.value.supervisors,
      defaultGroup: this.employeeGroup.defaultGroup ?? false,
      startScreenConfiguration: this.employeeGroup.startScreenConfiguration,
      isOnEdit: false
    };
    if (this.editMode) {
      await this.employeeService.updateEmployeeGroup(empGroup);
      this.deleteButtonConfig.disabled = false;
      this.employeeGroupForm.markAsPristine();
    } else {
      this.editMode = true;
      this.onEmployeeGroupChanging = true;
      await this.employeeService.addEmployeeGroup(empGroup);
      this.deleteButtonConfig.disabled = false;
      this.employeeGroupForm.markAsPristine();
    }
  }
  onActionButtonClick(buttonText: string) {
    if (buttonText === SAVE_CALL_BACK_IDENTIFIER) {
      this.onSubmit();
    }

    if (buttonText === DELETE_CALL_BACK_IDENTIFIER) {
      this.onEmployeeGroupDelete();
    }
  }

  //get methods
  getNewEmployeeGroupObj(): EmployeeGroupModel {
    return {
      employeeGroupId: '',
      name: '',
      employees: [],
      defaultGroup: false,
      startScreenConfiguration: [],
      isOnEdit: false,
      supervisors: []
    };
  }

  getEmployeeGroupTitle(): string {
    if (!this.editMode) {
      return $localize`:add a new employee group header@@newEmployeeGroup:New Employee Group`;
    } else if (this.employeeGroup) {
      return this.employeeGroup.name;
    } else return '';
  }

  async getEmployeesInEmployeeGroup(): Promise<string[]> {
    return await this.employeeService
      .getEmployeesInEmployeeGroup(this.currentEmpGroupID)
      .catch((error: HttpErrorResponse) => this.handleError(error));
  }
  getEmployeeName(employee: EmployeeModel | undefined): string {
    return employee && employee.name
      ? employee.name
      : employee?.userName || employee?.employeeId || '';
  }

  //handlers
  async canDeactivate(): Promise<boolean> {
    //If the User deleted an Employee Group and he has to leave the route without confirming.
    if (this.onEmployeeGroupChanging) {
      this.onEmployeeGroupChanging = false;
      return true;
    }
    //Checks if the user did some changes in the forms.
    if (this.employeeGroupForm.dirty) {
      return await opencanDeactivateDialog(this.dialogService);
    } else {
      return true;
    }
  }

  onEmployeeGroupDelete() {
    if (this.employeeGroup.employees.length > 0) {
      const title = $localize`:delete process@@deleteProcess:Delete process`;
      const msg = $localize`:emplyoee group delete confirmation text@@deleteConfirmationEmployeeGroup:There are employees assigned to this group. Do you really want to delete this group?`;
      const confirmButtonText = $localize`:button text delete@@buttonDelete:DELETE`;
      const cancelButtonText = $localize`:button text cancel@@buttonCancel:CANCEL`;

      const dialogData: ConfirmDeleteDialogData = {
        itemName: this.employeeGroup.name,
        title: title,
        message: msg,
        confirmText: toTitleCase(confirmButtonText),
        cancelText: toTitleCase(cancelButtonText)
      };
      this.dialogService.openConfirmDelete(dialogData).subscribe((result: boolean) => {
        if (result == true) {
          this.employeeService.deleteEmployeeGroup(this.employeeGroup.employeeGroupId);
          //Its for the canDeactivate() function. If confirmDeleteStatus = false the Confirmation Dialog will be shown.
          this.onEmployeeGroupChanging = true;
        }
      });
    } else {
      this.employeeService.deleteEmployeeGroup(this.employeeGroup.employeeGroupId);
      this.onEmployeeGroupChanging = true;
    }
  }
  handleError(error: HttpErrorResponse): string[] {
    this.loadingEmployeeGroup = true;
    this.snackbarService.showSnackBar(error?.message, '', 'error');

    return [];
  }

  convertToChipsElementModelArray(array: EmployeeModel[]): ChipsElementModel[] {
    const ChipsElementModelArray: ChipsElementModel[] = [];
    if (Array.isArray(array)) {
      array.forEach((element) => {
        ChipsElementModelArray.push({
          name: this.getEmployeeName(element),
          id: element.employeeId,
          url: EMPLOYEE_DETAILS_URL
        });
      });
    }
    return ChipsElementModelArray;
  }
}
